import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import "./GestioneDipendentiChiamata.css";
import Lf23Loader from "../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const BASE = `${API_URL}/gestione-dipendenti-chiamata`;

// "Oggi" locale del browser come stringa ISO (solo per i default dei date-picker;
// la regola "Annulla solo oggi" usa invece serverToday restituito dal backend).
const todayLocalIso = () => {
    const d = new Date();
    const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return z.toISOString().slice(0, 10);
};

const addDaysIso = (iso, n) => {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + n);
    const z = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000);
    return z.toISOString().slice(0, 10);
};

const fmtGiorno = (iso) => {
    if (!iso || iso.length < 10) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};

// Invio automatico: il job gira alle 23:00 e trasmette i turni del giorno successivo,
// quindi la richiesta per un turno parte alle 23:00 della sera precedente.
const fmtInvioPrevisto = (iso) => {
    if (!iso || iso.length < 10) return "—";
    return `${fmtGiorno(addDaysIso(iso, -1))} 23:00`;
};

// Data/ora correnti formattate "gg/mm/aaaa HH:MM" (timestamp degli invii manuali).
const nowDataOra = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Chiave ordinabile per il turno (data + ora inizio + ora fine), usata per ordinare
// dal turno più recente/futuro al più vecchio. Senza orario si usa "00:00".
const turnoSortKey = (r) => `${r.data} ${r.ora_inizio || "00:00"} ${r.ora_fine || "00:00"}`;

// Chiave ordinabile dalla stringa invio "DD/MM/YYYY HH:MM" -> "YYYYMMDD HH:MM". "" se assente.
const invioSortKey = (s) => {
    if (!s || !/^\d{2}\/\d{2}\/\d{4}/.test(s)) return "";
    const [d, m, rest] = s.split("/");
    const [y, hm = ""] = rest.split(" ");
    return `${y}${m}${d} ${hm}`;
};

// Valore ordinabile (stringa) di una riga storico per colonna. "" = vuoto (va sempre in fondo).
const storicoSortValue = (r, col) => {
    switch (col) {
        case "data": return r.data || "";
        case "operatore": return r.operatore || "";
        case "filiale": return (r.filiali || []).join(", ");
        case "commessa": return (r.commesse || []).join(", ");
        case "ora_inizio": return r.ora_inizio || "";
        case "ora_fine": return r.ora_fine || "";
        case "invio": return invioSortKey(r.inviata_il);
        case "stato": return r.stato || "";
        default: return "";
    }
};

// Valore ordinabile per il pannello "Turni di oggi da comunicare".
const panelSortValue = (r, col) => {
    switch (col) {
        case "operatore": return r.operatore || "";
        case "filiale": return (r.filiali || []).join(", ");
        case "commessa": return (r.commesse || []).join(", ");
        case "ora_inizio": return r.ora_inizio || "";
        case "ora_fine": return r.ora_fine || "";
        case "segnalazione": return r.segnalazione || "";
        default: return "";
    }
};

// Ordina le righe per colonna (stringa), celle vuote sempre in fondo. sortBy null => invariato.
const sortRows = (rows, sortBy, sortDir, valueFn) => {
    if (!sortBy) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
        const va = valueFn(a, sortBy);
        const vb = valueFn(b, sortBy);
        if (!va && !vb) return 0;
        if (!va) return 1;
        if (!vb) return -1;
        return va.localeCompare(vb, "it-IT", { numeric: true }) * dir;
    });
};

// Cicla lo stato di ordinamento per una colonna: asc -> desc -> default (null).
const cycleSort = (col, sortBy, setSortBy, sortDir, setSortDir) => {
    if (sortBy !== col) {
        setSortBy(col);
        setSortDir("asc");
    } else if (sortDir === "asc") {
        setSortDir("desc");
    } else {
        setSortBy(null);
    }
};

// Intestazione colonna ordinabile (generica): click cicla asc/desc/default, con indicatore.
const SortableTh = ({ col, sortBy, sortDir, onSort, children, style }) => (
    <th
        style={style}
        className={`gdc-th-sort${sortBy === col ? " active" : ""}`}
        onClick={() => onSort(col)}
        title="Ordina per questa colonna"
    >
        {children}
        <span className="gdc-sort-ind">
            {sortBy === col ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
        </span>
    </th>
);

const keyOf = (operatoreId, data) => `${operatoreId}|${data}`;

// "Annulla" consentito solo fino a 1 ora prima dell'inizio del turno.
// Senza orario di inizio si ricade sulla regola "solo giornata odierna".
const isAnnullabile = (r, serverToday) => {
    if (r.stato === "Annullata") return false;
    if (!r.ora_inizio || !/^\d{2}:\d{2}/.test(r.ora_inizio)) return r.data === serverToday;
    const [y, m, d] = r.data.split("-").map(Number);
    const [hh, mm] = r.ora_inizio.split(":").map(Number);
    const inizioTurno = new Date(y, m - 1, d, hh, mm);
    const limite = new Date(inizioTurno.getTime() - 60 * 60 * 1000); // -1 ora
    return new Date() < limite;
};

// Mostra un array di valori (filiali/commesse) come testo; "—" se vuoto.
const joinOr = (arr) => (Array.isArray(arr) && arr.length ? arr.join(", ") : "—");

// Opzioni react-select da un campo array delle righe (filiali/commesse).
const optsFromArrays = (rows, key) => {
    const set = new Set();
    rows.forEach((r) => (r[key] || []).forEach((v) => v && set.add(v)));
    return [...set]
        .sort((a, b) => a.localeCompare(b, "it-IT", { numeric: true }))
        .map((v) => ({ value: v, label: v }));
};

const IconMail = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M22 7l-10 6L2 7" />
    </svg>
);

const GestioneDipendentiChiamata = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState("storico");

    // Range temporale Storico: default SOLO oggi (i date-picker restano per andare indietro).
    const [dateTo, setDateTo] = useState(() => todayLocalIso());
    const [dateFrom, setDateFrom] = useState(() => todayLocalIso());

    // Storico
    const [serverToday, setServerToday] = useState(() => todayLocalIso());
    const [storicoRows, setStoricoRows] = useState([]);
    const [loadingStorico, setLoadingStorico] = useState(false);
    const [errStorico, setErrStorico] = useState("");

    // Ordinamento tabella storico: colonna + direzione. sortBy=null => ordinamento di default
    // (turno DESC, come mergedRows). Ciclo per colonna: asc -> desc -> default.
    const [sortBy, setSortBy] = useState(null);
    const [sortDir, setSortDir] = useState("asc");
    const toggleSort = (col) => cycleSort(col, sortBy, setSortBy, sortDir, setSortDir);

    // Ordinamento pannello "Turni di oggi da comunicare" (stato indipendente dallo storico).
    const [panelSortBy, setPanelSortBy] = useState(null);
    const [panelSortDir, setPanelSortDir] = useState("asc");
    const togglePanelSort = (col) => cycleSort(col, panelSortBy, setPanelSortBy, panelSortDir, setPanelSortDir);

    // Filtri
    const [opOptions, setOpOptions] = useState([]);
    const [fOperatore, setFOperatore] = useState([]);
    const [fFiliale, setFFiliale] = useState([]);
    const [fCommessa, setFCommessa] = useState([]);

    // Stato MOCK di sessione (Annulla + Nuovo invio): key `operatore_id|data` ->
    // { stato: "Inviata"|"Annullata", source: "db"|"manual", operatore, data }. Perso al refresh.
    const [sessionState, setSessionState] = useState({});

    // Modale conferma "Annulla chiamata": riga in attesa di conferma (null = chiuso)
    const [annullaTarget, setAnnullaTarget] = useState(null);

    // Sezione realtime "Turni di oggi da comunicare":
    //  - inviaTarget: riga in attesa di conferma invio (null = modale chiuso)
    //  - inviatiSezione: chiavi `operatore_id|data` già inviate in sessione (per nasconderle)
    const [inviaTarget, setInviaTarget] = useState(null);
    const [inviatiSezione, setInviatiSezione] = useState({});
    // Invio reale (solo Nuovo invio): stato di caricamento ed errore del POST
    const [sendingInvio, setSendingInvio] = useState(false);
    const [errInvio, setErrInvio] = useState("");
    // Annullamento reale
    const [annullando, setAnnullando] = useState(false);
    const [errAnnulla, setErrAnnulla] = useState("");

    // Modale "Nuovo invio"
    const [showModal, setShowModal] = useState(false);
    const [modalOperatori, setModalOperatori] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalSel, setModalSel] = useState(null);
    // Dati del turno da comporre a mano nel Nuovo invio (tutti opzionali)
    const [modalOraInizio, setModalOraInizio] = useState("");
    const [modalOraFine, setModalOraFine] = useState("");
    const [modalFiliale, setModalFiliale] = useState(null);
    const [modalCommessa, setModalCommessa] = useState(null);

    // Pianificazione (lazy)
    const [pianRows, setPianRows] = useState([]);
    const [pianLoaded, setPianLoaded] = useState(false);
    const [loadingPian, setLoadingPian] = useState(false);
    const [errPian, setErrPian] = useState("");

    // Pannello realtime "Turni di oggi da comunicare" (dati reali dalle API Gecos):
    //  - "Turno aggiunto dopo le 23:00" (turno di oggi creato dopo l'invio automatico)
    //  - "Timbratura senza turno previsto" (anomalia, riga evidenziata)
    const [daComunicare, setDaComunicare] = useState([]);
    const [loadingDaComunicare, setLoadingDaComunicare] = useState(true);
    // Al primo caricamento mostriamo tutto (pannello + storico) INSIEME, quando entrambe le
    // chiamate sono pronte, per evitare che lo storico appaia prima e il pannello ci salti sopra.
    // Dopo il primo load, i cambi-data ricaricano solo la tabella (pannello stabile).
    const [firstLoadDone, setFirstLoadDone] = useState(false);

    // Pannello "Turni di oggi da comunicare": dati reali, caricati una volta sola.
    // L'AbortController annulla davvero la richiesta su Gecos se si lascia la pagina.
    useEffect(() => {
        let cancelled = false;
        const controller = new AbortController();
        (async () => {
            try {
                const res = await axios.get(`${BASE}/turni-da-comunicare`, { signal: controller.signal });
                if (!cancelled) setDaComunicare(Array.isArray(res.data) ? res.data : []);
            } catch {
                /* non bloccante: il pannello resta vuoto (incluso l'annullamento) */
            } finally {
                if (!cancelled) setLoadingDaComunicare(false);
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, []);

    // Segna "primo caricamento fatto" quando pannello e storico sono entrambi pronti.
    useEffect(() => {
        if (!firstLoadDone && !loadingStorico && !loadingDaComunicare) setFirstLoadDone(true);
    }, [firstLoadDone, loadingStorico, loadingDaComunicare]);

    // Opzioni filtro Operatore (soli dipendenti a chiamata) — una volta sola
    useEffect(() => {
        let cancelled = false;
        const controller = new AbortController();
        (async () => {
            try {
                const res = await axios.get(`${BASE}/filtri`, { signal: controller.signal });
                if (!cancelled) setOpOptions(res.data?.operatori || []);
            } catch {
                /* non bloccante (incluso l'annullamento) */
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, []);

    // Storico: carica su mount e a ogni cambio range. Il `signal` (passato dall'effect)
    // annulla la richiesta su cambio range/unmount; le chiamate imperative lo omettono.
    const fetchStorico = useCallback(async (signal) => {
        if (!dateFrom || !dateTo) return;
        setLoadingStorico(true);
        setErrStorico("");
        try {
            const res = await axios.get(`${BASE}/storico`, { params: { dateFrom, dateTo }, signal });
            if (res.data?.today) setServerToday(res.data.today);
            setStoricoRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
        } catch (e) {
            if (axios.isCancel(e) || e.code === "ERR_CANCELED") return; // annullata: la subentra un'altra fetch
            setErrStorico(e.response?.data?.error || "Errore nel recupero dello storico.");
            setStoricoRows([]);
        } finally {
            if (!signal || !signal.aborted) setLoadingStorico(false);
        }
    }, [dateFrom, dateTo]);

    useEffect(() => {
        const controller = new AbortController();
        fetchStorico(controller.signal);
        return () => controller.abort();
    }, [fetchStorico]);

    // Pianificazione: carica alla prima apertura del tab
    const fetchPianificazione = useCallback(async (signal) => {
        setLoadingPian(true);
        setErrPian("");
        try {
            const res = await axios.get(`${BASE}/pianificazione`, { signal });
            setPianRows(Array.isArray(res.data) ? res.data : []);
            setPianLoaded(true);
        } catch (e) {
            if (axios.isCancel(e) || e.code === "ERR_CANCELED") return; // annullata (unmount/cambio tab)
            setErrPian(e.response?.data?.error || "Errore nel recupero della pianificazione.");
            setPianRows([]);
        } finally {
            if (!signal || !signal.aborted) setLoadingPian(false);
        }
    }, []);

    useEffect(() => {
        if (tab !== "pianificazione" || pianLoaded || loadingPian) return;
        const controller = new AbortController();
        fetchPianificazione(controller.signal);
        return () => controller.abort();
    }, [tab, pianLoaded, loadingPian, fetchPianificazione]);

    const filialeOptions = useMemo(() => optsFromArrays(storicoRows, "filiali"), [storicoRows]);
    const commessaOptions = useMemo(() => optsFromArrays(storicoRows, "commesse"), [storicoRows]);

    // Righe pianificazione da mostrare: dati reali ordinati per data turno. Le righe
    // bloccate (turno pianificato ma dipendente in ferie/assenza) sono calcolate lato
    // server dalle API Gecos e compaiono solo quando il caso reale esiste.
    const pianDisplayRows = useMemo(
        () => [...pianRows].sort((a, b) => a.data.localeCompare(b.data)),
        [pianRows]
    );

    // Righe ancora da inviare: escludo quelle già trasmesse in sessione, poi applico l'ordinamento.
    const daInviareRows = useMemo(() => {
        const filtered = daComunicare.filter((r) => !inviatiSezione[keyOf(r.operatore_id, r.data)]);
        return sortRows(filtered, panelSortBy, panelSortDir, panelSortValue);
    }, [daComunicare, inviatiSezione, panelSortBy, panelSortDir]);

    // Righe DB + righe manuali (nuovo invio) nel range, con STATO da sessione, ordinate.
    const mergedRows = useMemo(() => {
        const dbKeys = new Set(storicoRows.map((r) => keyOf(r.operatore_id, r.data)));
        const manual = Object.entries(sessionState)
            .filter(
                ([k, v]) =>
                    v.source === "manual" && !dbKeys.has(k) && v.data >= dateFrom && v.data <= dateTo
            )
            .map(([k, v]) => ({
                operatore_id: k.split("|")[0],
                operatore: v.operatore,
                data: v.data,
                filiali: v.filiali || [],
                commesse: v.commesse || [],
                ora_inizio: v.ora_inizio || null,
                ora_fine: v.ora_fine || null,
                inviata_il: v.inviata_il || null,
            }));
        const all = [
            ...manual,
            ...storicoRows.map((r) => ({
                operatore_id: r.operatore_id,
                operatore: r.operatore,
                data: r.data,
                filiali: r.filiali || [],
                commesse: r.commesse || [],
                ora_inizio: r.ora_inizio || null,
                ora_fine: r.ora_fine || null,
                stato: r.stato, // dal backend: "Inviata"/"Annullata" se c'è un invio reale
                inviata_il: r.inviata_il || null, // ora reale di invio (dal backend)
            })),
        ];
        return all
            .map((r) => ({
                ...r,
                // Stato reale: "Inviata"/"Annullata" solo se c'è un invio vero (dal backend o dalla
                // sessione); altrimenti "Da inviare" (default veritiero, niente più "Inviata" fittizia).
                stato: sessionState[keyOf(r.operatore_id, r.data)]?.stato ?? r.stato ?? "Da inviare",
            }))
            // Ordino per turno (data + ora inizio + ora fine) decrescente: i turni più
            // recenti/futuri in cima (così gli annullabili stanno in alto); a parità, per operatore.
            .sort(
                (a, b) =>
                    turnoSortKey(b).localeCompare(turnoSortKey(a)) ||
                    a.operatore.localeCompare(b.operatore, "it-IT")
            );
    }, [storicoRows, sessionState, dateFrom, dateTo]);

    // Filtri client-side: Operatore (match esatto) + Filiale/Commessa (ANY-of).
    const visibleRows = useMemo(() => {
        const selOp = new Set(fOperatore.map((o) => o.value));
        const selFil = new Set(fFiliale.map((o) => o.value));
        const selCom = new Set(fCommessa.map((o) => o.value));
        const anyMatch = (arr, sel) => sel.size === 0 || (arr || []).some((v) => sel.has(v));
        const filtered = mergedRows.filter(
            (r) =>
                (selOp.size === 0 || selOp.has(r.operatore_id)) &&
                anyMatch(r.filiali, selFil) &&
                anyMatch(r.commesse, selCom)
        );
        // Nessuna colonna selezionata: mantieni l'ordinamento di default di mergedRows (turno DESC).
        return sortRows(filtered, sortBy, sortDir, storicoSortValue);
    }, [mergedRows, fOperatore, fFiliale, fCommessa, sortBy, sortDir]);

    // Click su "Annulla": apre la conferma (l'annullamento effettivo avviene in confirmAnnulla).
    const handleAnnulla = (r) => {
        if (!isAnnullabile(r, serverToday)) return;
        setAnnullaTarget(r);
    };

    const confirmAnnulla = async () => {
        const r = annullaTarget;
        if (!r) return;
        setAnnullando(true);
        setErrAnnulla("");
        try {
            await axios.post(`${BASE}/annulla`, {
                operatore_id: r.operatore_id,
                data: r.data,
                ora_inizio: r.ora_inizio || null,
                ora_fine: r.ora_fine || null,
                filiale: r.filiali?.[0] || null,
                commessa: r.commesse?.[0] || null,
            });
        } catch (e) {
            setErrAnnulla(e.response?.data?.error || "Annullamento non riuscito.");
            setAnnullando(false);
            return; // non marco come annullata se il POST fallisce
        }
        setAnnullando(false);
        const k = keyOf(r.operatore_id, r.data);
        setSessionState((p) => ({
            ...p,
            // preservo eventuali campi dell'invio manuale (filiali/commesse/ora/inviata_il)
            [k]: { ...p[k], stato: "Annullata", source: p[k]?.source || "db", operatore: r.operatore, data: r.data },
        }));
        setAnnullaTarget(null);
        fetchStorico(); // ricarico: lo stato "Annullata" arriva dal backend e persiste al refresh
    };

    // Sezione realtime: click su "Invia" apre la conferma; l'invio è reale (come il Nuovo invio).
    const handleInviaSezione = (r) => {
        setErrInvio("");
        setInviaTarget({ ...r, _realSend: true });
    };

    const confirmInviaSezione = async () => {
        const r = inviaTarget;
        if (!r) return;
        // Invio reale (Nuovo invio, pannello, o bottone Invia dello storico): _realSend => POST.
        if (r._realSend) {
            setSendingInvio(true);
            setErrInvio("");
            try {
                await axios.post(`${BASE}/nuovo-invio`, {
                    operatore_id: r.operatore_id,
                    data: r.data, // giorno della prestazione (riga storico/pannello)
                    // solo per la visualizzazione nello storico (NON entrano nell'XML)
                    ora_inizio: r.ora_inizio || null,
                    ora_fine: r.ora_fine || null,
                    filiale: r.filiali?.[0] || null,
                    commessa: r.commesse?.[0] || null,
                });
            } catch (e) {
                setErrInvio(e.response?.data?.error || "Invio non riuscito.");
                setSendingInvio(false);
                return; // non marco come inviata se il POST fallisce
            }
            setSendingInvio(false);
            // Ricarico lo storico: la riga persistita arriva dal backend e sopravvive al refresh.
            fetchStorico();
        }
        const k = keyOf(r.operatore_id, r.data);
        setInviatiSezione((p) => ({ ...p, [k]: true }));
        // La riga inviata confluisce nello storico come "Inviata" (manuale), con i suoi dati.
        setSessionState((p) => ({
            ...p,
            [k]: {
                stato: "Inviata",
                source: "manual",
                operatore: r.operatore,
                data: r.data,
                filiali: r.filiali,
                commesse: r.commesse,
                ora_inizio: r.ora_inizio,
                ora_fine: r.ora_fine,
                inviata_il: nowDataOra(),
            },
        }));
        setInviaTarget(null);
    };

    const openModal = async () => {
        setShowModal(true);
        setModalSel(null);
        setModalOraInizio("");
        setModalOraFine("");
        setModalFiliale(null);
        setModalCommessa(null);
        setModalLoading(true);
        try {
            const res = await axios.get(`${BASE}/nuovo-invio-operatori`);
            if (res.data?.today) setServerToday(res.data.today);
            const sentIds = new Set(
                Object.entries(sessionState)
                    .filter(([, v]) => v.source === "manual")
                    .map(([k]) => k.split("|")[0])
            );
            setModalOperatori((res.data?.operatori || []).filter((o) => !sentIds.has(o.value)));
        } catch {
            setModalOperatori([]);
        } finally {
            setModalLoading(false);
        }
    };

    // "Nuovo invio": apre la conferma (riusa il modale inviaTarget). L'invio effettivo
    // avviene in confirmInviaSezione, che inserisce la riga nello storico come "Inviata".
    const handleInvia = () => {
        if (!modalSel || !oreNuovoInvioValide) return;
        setErrInvio("");
        setInviaTarget({
            _realSend: true, // il Nuovo invio spedisce davvero la comunicazione
            operatore_id: modalSel.value,
            operatore: modalSel.label,
            data: serverToday,
            filiali: modalFiliale ? [modalFiliale.value] : [],
            commesse: modalCommessa ? [modalCommessa.value] : [],
            ora_inizio: modalOraInizio || null,
            ora_fine: modalOraFine || null,
        });
        setModalSel(null);
        setShowModal(false);
    };

    // Validazione Nuovo invio: ora inizio/fine obbligatorie e coerenti (fine > inizio).
    const oreNuovoInvioValide = !!modalOraInizio && !!modalOraFine && modalOraFine > modalOraInizio;
    const nuovoInvioValido = !!modalSel && oreNuovoInvioValide;

    const renderStorico = () => {
        // Primo caricamento: un solo loader finché pannello + storico non sono ENTRAMBI pronti,
        // così compaiono insieme (niente storico prima e pannello che ci salta sopra).
        if (!firstLoadDone) {
            return (
                <Lf23Loader testo="Caricamento in corso..." />
            );
        }
        return (
        <>
            {daInviareRows.length > 0 && (
                <div className="gdc-realtime-panel">
                    <div className="gdc-realtime-head">
                        <span className="gdc-realtime-dot" />
                        <div>
                            <h3>Turni di oggi da comunicare</h3>
                            <p>
                                Casi di dipendenti a chiamata da comunicare manualmente: turni di oggi
                                <strong> non ancora comunicati</strong> automaticamente, oppure dipendenti
                                che hanno <strong>timbrato senza un turno previsto</strong> (stanno
                                lavorando — riga evidenziata). Trasmetti la comunicazione obbligatoria
                                con il tasto <strong>Invia</strong>.
                            </p>
                        </div>
                    </div>
                    <div className="gdc-table-wrap">
                        <table className="gdc-table">
                            <thead>
                                <tr>
                                    <SortableTh col="operatore" sortBy={panelSortBy} sortDir={panelSortDir} onSort={togglePanelSort}>Operatore</SortableTh>
                                    <SortableTh col="filiale" sortBy={panelSortBy} sortDir={panelSortDir} onSort={togglePanelSort}>Filiale</SortableTh>
                                    <SortableTh col="commessa" sortBy={panelSortBy} sortDir={panelSortDir} onSort={togglePanelSort}>Commessa</SortableTh>
                                    <SortableTh col="ora_inizio" sortBy={panelSortBy} sortDir={panelSortDir} onSort={togglePanelSort} style={{ width: "10%" }}>Ora inizio</SortableTh>
                                    <SortableTh col="ora_fine" sortBy={panelSortBy} sortDir={panelSortDir} onSort={togglePanelSort} style={{ width: "10%" }}>Ora fine</SortableTh>
                                    <SortableTh col="segnalazione" sortBy={panelSortBy} sortDir={panelSortDir} onSort={togglePanelSort} style={{ width: "20%" }}>Segnalazione</SortableTh>
                                    <th style={{ width: "12%" }} className="gdc-action-col">Azione</th>
                                </tr>
                            </thead>
                            <tbody>
                                {daInviareRows.map((r) => (
                                    <tr
                                        key={keyOf(r.operatore_id, r.data)}
                                        className={r.anomalia ? "gdc-row-bloccata" : ""}
                                    >
                                        <td>{r.operatore}</td>
                                        <td>{joinOr(r.filiali)}</td>
                                        <td>{joinOr(r.commesse)}</td>
                                        <td>{r.ora_inizio || "—"}</td>
                                        <td>{r.ora_fine || "—"}</td>
                                        <td>
                                            {r.anomalia ? (
                                                <span className="gdc-invio-bloccato">
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                                        <line x1="12" y1="9" x2="12" y2="13" />
                                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                                    </svg>
                                                    {r.segnalazione}
                                                </span>
                                            ) : (
                                                <span className="gdc-segnalazione-std">{r.segnalazione}</span>
                                            )}
                                        </td>
                                        <td className="gdc-action-col">
                                            <button
                                                type="button"
                                                className="gdc-invia-btn"
                                                onClick={() => handleInviaSezione(r)}
                                                title="Invia la comunicazione obbligatoria"
                                            >
                                                <IconMail size={14} />
                                                Invia
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="gdc-toolbar">
                <div className="gdc-filter">
                    <label>Da</label>
                    <DatePickerModal value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} max={dateTo} label="Data inizio" />
                </div>
                <div className="gdc-filter">
                    <label>A</label>
                    <DatePickerModal value={dateTo} onChange={(e) => setDateTo(e.target.value)} min={dateFrom} label="Data fine" />
                </div>
                <div className="gdc-filter gdc-filter-select">
                    <label>Operatore</label>
                    <Select
                        isMulti
                        options={opOptions}
                        value={fOperatore}
                        onChange={(v) => setFOperatore(v || [])}
                        classNamePrefix="acr-select"
                        placeholder="Tutti"
                        closeMenuOnSelect={false}
                        noOptionsMessage={() => "Nessun operatore"}
                    />
                </div>
                <div className="gdc-filter gdc-filter-select">
                    <label>Filiale</label>
                    <Select
                        isMulti
                        options={filialeOptions}
                        value={fFiliale}
                        onChange={(v) => setFFiliale(v || [])}
                        classNamePrefix="acr-select"
                        placeholder="Tutte"
                        closeMenuOnSelect={false}
                        noOptionsMessage={() => "Nessuna filiale"}
                    />
                </div>
                <div className="gdc-filter gdc-filter-select">
                    <label>Commessa</label>
                    <Select
                        isMulti
                        options={commessaOptions}
                        value={fCommessa}
                        onChange={(v) => setFCommessa(v || [])}
                        classNamePrefix="acr-select"
                        placeholder="Tutte"
                        closeMenuOnSelect={false}
                        noOptionsMessage={() => "Nessuna commessa"}
                    />
                </div>
                <div className="gdc-toolbar-right">
                    <button type="button" className="gdc-btn-nuovo" onClick={openModal}>
                        <IconMail size={16} />
                        Nuovo invio
                    </button>
                </div>
            </div>

            {errStorico && <div className="gdc-error" role="alert">{errStorico}</div>}

            <div className="gdc-table-wrap">
                {loadingStorico ? (
                    <Lf23Loader testo="Caricamento storico in corso..." />
                ) : visibleRows.length === 0 ? (
                    <div className="gdc-empty">Nessun invio per i filtri selezionati.</div>
                ) : (
                    <table className="gdc-table">
                        <thead>
                            <tr>
                                <SortableTh col="data" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} style={{ width: "11%" }}>Data</SortableTh>
                                <SortableTh col="operatore" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Operatore</SortableTh>
                                <SortableTh col="filiale" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Filiale</SortableTh>
                                <SortableTh col="commessa" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Commessa</SortableTh>
                                <SortableTh col="ora_inizio" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} style={{ width: "9%" }}>Ora inizio</SortableTh>
                                <SortableTh col="ora_fine" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} style={{ width: "9%" }}>Ora fine</SortableTh>
                                <SortableTh col="invio" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} style={{ width: "15%" }}>Data/ora invio</SortableTh>
                                <SortableTh col="stato" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} style={{ width: "12%" }}>Stato</SortableTh>
                                <th style={{ width: "10%" }} className="gdc-action-col">Azione</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((r) => {
                                const inviata = r.stato === "Inviata";
                                const annullata = r.stato === "Annullata";
                                // "Da inviare" = né inviata né annullata.
                                const annullabile = inviata && isAnnullabile(r, serverToday);
                                // Data/ora invio: SOLO se realmente inviata (timestamp reale). Altrimenti "—".
                                const dataInvio = inviata ? r.inviata_il : null;
                                return (
                                    <tr key={keyOf(r.operatore_id, r.data)}>
                                        <td>{fmtGiorno(r.data)}</td>
                                        <td>{r.operatore}</td>
                                        <td>{joinOr(r.filiali)}</td>
                                        <td>{joinOr(r.commesse)}</td>
                                        <td>{r.ora_inizio || "—"}</td>
                                        <td>{r.ora_fine || "—"}</td>
                                        <td>
                                            {dataInvio ? (
                                                <span className="gdc-invio-previsto">
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="10" />
                                                        <polyline points="12 6 12 12 16 14" />
                                                    </svg>
                                                    {dataInvio}
                                                </span>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td>
                                            {annullata ? (
                                                <span className="gdc-stato gdc-stato-annullata">
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                                                        <circle cx="12" cy="12" r="10" />
                                                        <line x1="15" y1="9" x2="9" y2="15" />
                                                        <line x1="9" y1="9" x2="15" y2="15" />
                                                    </svg>
                                                    Annullata
                                                </span>
                                            ) : inviata ? (
                                                <span className="gdc-stato gdc-stato-inviata">
                                                    <IconMail size={13} />
                                                    Inviata
                                                </span>
                                            ) : (
                                                <span className="gdc-stato gdc-stato-dainviare">
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="10" />
                                                        <polyline points="12 6 12 12 16 14" />
                                                    </svg>
                                                    Da inviare
                                                </span>
                                            )}
                                        </td>
                                        <td className="gdc-action-col">
                                            {inviata ? (
                                                <button
                                                    type="button"
                                                    className="gdc-annulla-btn"
                                                    disabled={!annullabile}
                                                    onClick={() => handleAnnulla(r)}
                                                    title={
                                                        annullabile
                                                            ? "Annulla la chiamata"
                                                            : "Annullabile solo fino a 1 ora prima dell'inizio del turno"
                                                    }
                                                >
                                                    Annulla
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="gdc-invia-btn"
                                                    onClick={() => handleInviaSezione(r)}
                                                    title={annullata ? "Reinvia la comunicazione" : "Invia la comunicazione obbligatoria"}
                                                >
                                                    <IconMail size={14} />
                                                    Invia
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </>
        );
    };

    const renderPianificazione = () => (
        <>
            <div className="gdc-pian-hint">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>
                    Dipendenti a chiamata con turno previsto per il <strong>giorno seguente</strong>. Le
                    richieste vengono trasmesse automaticamente da un job giornaliero delle <strong>23:00</strong>,
                    la sera precedente il turno (vedi colonna <strong>Invio previsto</strong>). Le righe
                    evidenziate hanno un turno pianificato ma il dipendente non è disponibile (es. ferie):
                    sono un'incoerenza gestionale e <strong>non verranno inviate</strong>.
                </span>
            </div>
            {errPian && <div className="gdc-error" role="alert">{errPian}</div>}
            <div className="gdc-table-wrap">
                {loadingPian ? (
                    <Lf23Loader testo="Caricamento pianificazione in corso..." />
                ) : pianDisplayRows.length === 0 ? (
                    <div className="gdc-empty">Nessun turno pianificato per domani.</div>
                ) : (
                    <table className="gdc-table">
                        <thead>
                            <tr>
                                <th style={{ width: "13%" }}>Data turno</th>
                                <th style={{ width: "16%" }}>Invio previsto</th>
                                <th>Operatore</th>
                                <th>Filiale</th>
                                <th>Commessa</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pianDisplayRows.map((r, i) => (
                                <tr
                                    key={`${r.operatore_id}|${r.data}|${i}`}
                                    className={r.bloccato ? "gdc-row-bloccata" : ""}
                                >
                                    <td>{fmtGiorno(r.data)}</td>
                                    <td>
                                        {r.bloccato ? (
                                            <span
                                                className="gdc-invio-bloccato"
                                                title={
                                                    r.motivo_blocco
                                                        ? `Turno pianificato ma dipendente non disponibile (${r.motivo_blocco}): nessuna comunicazione verrà inviata.`
                                                        : "Turno pianificato ma dipendente non disponibile: nessuna comunicazione verrà inviata."
                                                }
                                            >
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                                    <line x1="12" y1="9" x2="12" y2="13" />
                                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                                </svg>
                                                Non inviato{r.motivo_blocco ? ` — ${r.motivo_blocco}` : ""}
                                            </span>
                                        ) : (
                                            <span className="gdc-invio-previsto">
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <polyline points="12 6 12 12 16 14" />
                                                </svg>
                                                {fmtInvioPrevisto(r.data)}
                                            </span>
                                        )}
                                    </td>
                                    <td>{r.operatore}</td>
                                    <td>{r.filiale}</td>
                                    <td>{r.commessa}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );

    return (
        <div className="gdc-page">
            <div className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate("/automazione")}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <h2>Gestione Dipendenti a Chiamata</h2>
                        <p className="header-subtitle">
                            Storico invii e pianificazione turni dei dipendenti a chiamata.
                        </p>
                    </div>
                </div>
            </div>

            <div className="gdc-tabs">
                <button type="button" className={`gdc-tab ${tab === "storico" ? "active" : ""}`} onClick={() => setTab("storico")}>
                    Storico
                </button>
                <button type="button" className={`gdc-tab ${tab === "pianificazione" ? "active" : ""}`} onClick={() => setTab("pianificazione")}>
                    Pianificazione
                </button>
            </div>

            <div className="gdc-main">
                {tab === "storico" ? renderStorico() : renderPianificazione()}
            </div>

            {showModal && (
                <div className="gdc-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="gdc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="gdc-modal-header">
                            <div className="gdc-modal-icon">
                                <IconMail size={22} />
                            </div>
                            <h3>Nuovo invio</h3>
                            <button type="button" className="gdc-modal-close" onClick={() => setShowModal(false)} aria-label="Chiudi">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="gdc-modal-body">
                            <label className="gdc-modal-label">Operatore</label>
                            <Select
                                options={modalOperatori}
                                value={modalSel}
                                onChange={setModalSel}
                                classNamePrefix="acr-select"
                                placeholder={modalLoading ? "Caricamento…" : "Seleziona operatore…"}
                                isLoading={modalLoading}
                                isDisabled={modalLoading}
                                noOptionsMessage={() => "Nessun operatore disponibile per l'invio"}
                            />
                            <p className="gdc-modal-hint">
                                Operatori a chiamata con contratto attivo non ancora trasmessi per la giornata di oggi.
                            </p>

                            <div className="gdc-modal-divider">Turno</div>
                            <div className="gdc-modal-grid2">
                                <div>
                                    <label className="gdc-modal-label">Ora inizio <span className="gdc-req">*</span></label>
                                    <input
                                        type="time"
                                        className="gdc-modal-time"
                                        value={modalOraInizio}
                                        onChange={(e) => setModalOraInizio(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="gdc-modal-label">Ora fine <span className="gdc-req">*</span></label>
                                    <input
                                        type="time"
                                        className="gdc-modal-time"
                                        value={modalOraFine}
                                        onChange={(e) => setModalOraFine(e.target.value)}
                                    />
                                </div>
                            </div>
                            {modalOraInizio && modalOraFine && !oreNuovoInvioValide && (
                                <p className="gdc-modal-err">L'ora fine deve essere successiva all'ora inizio.</p>
                            )}

                            <label className="gdc-modal-label">Filiale</label>
                            <Select
                                options={filialeOptions}
                                value={modalFiliale}
                                onChange={setModalFiliale}
                                classNamePrefix="acr-select"
                                placeholder="Seleziona filiale…"
                                isClearable
                                noOptionsMessage={() => "Nessuna filiale"}
                            />

                            <label className="gdc-modal-label" style={{ marginTop: 12 }}>Commessa</label>
                            <Select
                                options={commessaOptions}
                                value={modalCommessa}
                                onChange={setModalCommessa}
                                classNamePrefix="acr-select"
                                placeholder="Seleziona commessa…"
                                isClearable
                                noOptionsMessage={() => "Nessuna commessa"}
                            />
                        </div>
                        <div className="gdc-modal-actions">
                            <button type="button" className="gdc-btn-secondary" onClick={() => setShowModal(false)}>
                                Annulla
                            </button>
                            <button type="button" className="gdc-btn-primary" onClick={handleInvia} disabled={!nuovoInvioValido}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 2L11 13" />
                                    <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                                </svg>
                                Invia
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {annullaTarget && (
                <div className="gdc-modal-overlay" onClick={() => { setAnnullaTarget(null); setErrAnnulla(""); }}>
                    <div className="gdc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="gdc-modal-header">
                            <div className="gdc-modal-icon gdc-modal-icon-warn">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                    <line x1="12" y1="9" x2="12" y2="13" />
                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                            </div>
                            <h3>Conferma annullamento chiamata</h3>
                            <button type="button" className="gdc-modal-close" disabled={annullando} onClick={() => { setAnnullaTarget(null); setErrAnnulla(""); }} aria-label="Chiudi">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="gdc-modal-body">
                            <p className="gdc-confirm-text">
                                Sei sicuro di voler annullare la chiamata del dipendente{" "}
                                <strong>{annullaTarget.operatore}</strong> del giorno{" "}
                                <strong>{fmtGiorno(annullaTarget.data)}</strong>?
                            </p>
                            <p className="gdc-modal-hint">
                                Verrà trasmessa la comunicazione obbligatoria con procedura di
                                cancellazione chiamata. L'operazione non è reversibile.
                            </p>
                            {errAnnulla && <p className="gdc-modal-err">{errAnnulla}</p>}
                        </div>
                        <div className="gdc-modal-actions">
                            <button type="button" className="gdc-btn-secondary" disabled={annullando} onClick={() => { setAnnullaTarget(null); setErrAnnulla(""); }}>
                                Indietro
                            </button>
                            <button type="button" className="gdc-btn-danger" onClick={confirmAnnulla} disabled={annullando}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="15" y1="9" x2="9" y2="15" />
                                    <line x1="9" y1="9" x2="15" y2="15" />
                                </svg>
                                {annullando ? "Annullamento…" : "Conferma annullamento"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {inviaTarget && (
                <div className="gdc-modal-overlay" onClick={() => setInviaTarget(null)}>
                    <div className="gdc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="gdc-modal-header">
                            <div className="gdc-modal-icon">
                                <IconMail size={22} />
                            </div>
                            <h3>Conferma invio comunicazione</h3>
                            <button type="button" className="gdc-modal-close" disabled={sendingInvio} onClick={() => { setInviaTarget(null); setErrInvio(""); }} aria-label="Chiudi">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="gdc-modal-body">
                            <p className="gdc-confirm-text">
                                Sei sicuro di voler inviare la comunicazione obbligatoria per il dipendente{" "}
                                <strong>{inviaTarget.operatore}</strong>{" "}
                                {inviaTarget.ora_inizio
                                    ? `per il turno delle ${inviaTarget.ora_inizio} di`
                                    : "per la giornata di"}{" "}
                                <strong>{fmtGiorno(inviaTarget.data)}</strong>?
                            </p>
                            <p className="gdc-modal-hint">
                                Verrà trasmessa la comunicazione obbligatoria con procedura di
                                comunicazione chiamata.
                            </p>
                            {errInvio && <p className="gdc-modal-err">{errInvio}</p>}
                        </div>
                        <div className="gdc-modal-actions">
                            <button type="button" className="gdc-btn-secondary" disabled={sendingInvio} onClick={() => { setInviaTarget(null); setErrInvio(""); }}>
                                Indietro
                            </button>
                            <button type="button" className="gdc-btn-primary" onClick={confirmInviaSezione} disabled={sendingInvio}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 2L11 13" />
                                    <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                                </svg>
                                {sendingInvio ? "Invio in corso…" : "Conferma invio"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestioneDipendentiChiamata;
